import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Save, Trash2, GripVertical, Mail, MessageSquare, CheckCircle, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface WorkflowStep {
  id: string;
  type: "action" | "condition" | "delay";
  action_type?: string;
  condition?: string;
  delay_minutes?: number;
  config: Record<string, any>;
}

interface WorkflowBuilderProps {
  workflowId: string | null;
}

function SortableStep({ step, onUpdate, onDelete }: { step: WorkflowStep; onUpdate: (step: WorkflowStep) => void; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStepIcon = (type: string) => {
    switch (type) {
      case "send_email": return <Mail className="h-4 w-4" />;
      case "send_notification": return <MessageSquare className="h-4 w-4" />;
      case "create_task": return <CheckCircle className="h-4 w-4" />;
      case "webhook": return <Zap className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="glass-card p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <Badge variant="outline" className="gap-2">
          {getStepIcon(step.action_type || "")}
          {step.action_type?.replace(/_/g, " ").toUpperCase()}
        </Badge>
        <Button variant="ghost" size="icon" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {step.type === "action" && (
        <div className="space-y-2 ml-8">
          <Select
            value={step.action_type}
            onValueChange={(value) => onUpdate({ ...step, action_type: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="send_email">Send Email</SelectItem>
              <SelectItem value="send_notification">Send Notification</SelectItem>
              <SelectItem value="create_task">Create Task</SelectItem>
              <SelectItem value="webhook">Call Webhook</SelectItem>
            </SelectContent>
          </Select>

          {step.action_type === "send_email" && (
            <>
              <Input
                placeholder="Email subject"
                value={step.config.subject || ""}
                onChange={(e) => onUpdate({ ...step, config: { ...step.config, subject: e.target.value } })}
              />
              <Textarea
                placeholder="Email body"
                value={step.config.body || ""}
                onChange={(e) => onUpdate({ ...step, config: { ...step.config, body: e.target.value } })}
              />
            </>
          )}

          {step.action_type === "webhook" && (
            <Input
              placeholder="Webhook URL"
              value={step.config.url || ""}
              onChange={(e) => onUpdate({ ...step, config: { ...step.config, url: e.target.value } })}
            />
          )}
        </div>
      )}

      {step.type === "delay" && (
        <div className="ml-8">
          <Input
            type="number"
            placeholder="Delay in minutes"
            value={step.delay_minutes || ""}
            onChange={(e) => onUpdate({ ...step, delay_minutes: parseInt(e.target.value) })}
          />
        </div>
      )}
    </div>
  );
}

export const WorkflowBuilder = ({ workflowId }: WorkflowBuilderProps) => {
  const [workflowName, setWorkflowName] = useState("");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState("");
  const [steps, setSteps] = useState<WorkflowStep[]>([]);
  const queryClient = useQueryClient();

  const { data: workflow } = useQuery({
    queryKey: ["workflow", workflowId],
    queryFn: async () => {
      if (!workflowId) return null;
      const { data, error } = await supabase
        .from("automation_workflows")
        .select("*")
        .eq("id", workflowId)
        .single();
      
      if (error) throw error;
      
      setWorkflowName(data.name);
      setDescription(data.description || "");
      setTriggerType(data.trigger_event);
      setSteps((data.actions as any) || []);
      
      return data;
    },
    enabled: !!workflowId,
  });

  const saveWorkflowMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const workflowData = {
        name: workflowName,
        description,
        trigger_event: triggerType,
        actions: steps as any,
        user_id: user.id,
      };

      if (workflowId) {
        const { error } = await supabase
          .from("automation_workflows")
          .update(workflowData)
          .eq("id", workflowId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("automation_workflows")
          .insert(workflowData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Workflow saved successfully");
      queryClient.invalidateQueries({ queryKey: ["automation-workflows"] });
    },
    onError: (error) => {
      toast.error("Failed to save workflow: " + error.message);
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addStep = (type: "action" | "delay") => {
    const newStep: WorkflowStep = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      action_type: type === "action" ? "send_email" : undefined,
      delay_minutes: type === "delay" ? 5 : undefined,
      config: {},
    };
    setSteps([...steps, newStep]);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <h2 className="text-2xl font-bold">Workflow Builder</h2>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Workflow Name</Label>
            <Input 
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="Enter workflow name"
            />
          </div>

          <div>
            <Label>Trigger Event</Label>
            <Select value={triggerType} onValueChange={setTriggerType}>
              <SelectTrigger>
                <SelectValue placeholder="Select trigger" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new_client">New Client Added</SelectItem>
                <SelectItem value="new_project">New Project Created</SelectItem>
                <SelectItem value="invoice_created">Invoice Created</SelectItem>
                <SelectItem value="invoice_paid">Invoice Paid</SelectItem>
                <SelectItem value="project_status_changed">Project Status Changed</SelectItem>
                <SelectItem value="task_completed">Task Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this workflow does"
            rows={2}
          />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Workflow Steps</h3>
          <div className="flex gap-2">
            <Button onClick={() => addStep("action")} variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Action
            </Button>
            <Button onClick={() => addStep("delay")} variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Delay
            </Button>
          </div>
        </div>

        {steps.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No steps added yet. Click "Add Action" to get started.
          </div>
        ) : (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <SortableStep
                    key={step.id}
                    step={step}
                    onUpdate={(updatedStep) => {
                      const newSteps = [...steps];
                      newSteps[index] = updatedStep;
                      setSteps(newSteps);
                    }}
                    onDelete={() => setSteps(steps.filter((_, i) => i !== index))}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline">Cancel</Button>
        <Button onClick={() => saveWorkflowMutation.mutate()} disabled={!workflowName || !triggerType}>
          <Save className="mr-2 h-4 w-4" />
          {workflowId ? "Update" : "Save"} Workflow
        </Button>
      </div>
    </div>
  );
};